import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link, useLocation } from "wouter";
import { User2, GraduationCap, Phone, PhoneCall } from "lucide-react";
import {
  createChildSchema,
  type CreateChildInput,
} from "@shared/validation/child.schema";
import { useCreateChild } from "../lib/api/useChildren";
import { useToast } from "../lib/toast/ToastProvider";
import { ApiError } from "../lib/api/client";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { SectionCard } from "../components/ui/SectionCard";
import { Field } from "../components/ui/Field";
import { Button } from "../components/ui/Button";
import { LegalNameField } from "../components/child/ChildNameFields";
import {
  NICKNAME_HINT,
  NICKNAME_LABEL,
  NICKNAME_PLACEHOLDER,
} from "../components/child/childNameCopy";

export function ChildNew() {
  const [, setLocation] = useLocation();
  const createMutation = useCreateChild();
  const { publish } = useToast();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<CreateChildInput>({
    resolver: zodResolver(createChildSchema),
    defaultValues: {
      name: "",
      legalName: null,
      dateOfBirth: null,
      grade: null,
      additionalNotes: null,
      isEnrolled: true,
      schoolName: null,
      schoolPhone: null,
      teacherName: null,
      schoolNursePhone: null,
      emergencyContactName: null,
      emergencyContactPhone: null,
      emergencyContactRelationship: null,
      secondaryEmergencyContactName: null,
      secondaryEmergencyContactPhone: null,
      secondaryEmergencyContactRelationship: null,
    },
  });

  const onSubmit = async (data: CreateChildInput) => {
    try {
      const created = await createMutation.mutateAsync(data);
      publish({
        variant: "success",
        title: "Child profile created successfully",
      });
      setLocation(`/child/${created.id}`);
    } catch (err) {
      publish({
        variant: "error",
        title: "Failed to create child profile",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  return (
    <AppShell maxWidth="2xl">
      <PlayfulHero
        accent="sky"
        decor="compact"
        art="list"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        title="Add a child profile"
        description="Build one always-current profile so any caregiver has exactly what they need."
      />

      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-6 sm:mt-10 sm:space-y-8">
        <SectionCard icon={User2} title="Basic Info" accent="sky">
          <div className="space-y-4">
            <Field
              label={NICKNAME_LABEL}
              htmlFor="child-name"
              error={errors.name?.message}
              hint={NICKNAME_HINT}
            >
              <input
                id="child-name"
                type="text"
                autoComplete="off"
                {...register("name")}
                className={INPUT_CLASS}
                placeholder={NICKNAME_PLACEHOLDER}
              />
            </Field>
            <LegalNameField
              id="child-legal-name"
              registration={register("legalName")}
              error={errors.legalName?.message}
              hasValue={false}
            />
            <Field
              label="Date of Birth"
              htmlFor="child-dob"
              error={errors.dateOfBirth?.message}
            >
              <input
                id="child-dob"
                type="date"
                {...register("dateOfBirth", {
                  setValueAs: (v) => (v === "" ? null : v),
                })}
                className={INPUT_CLASS}
              />
            </Field>
            <Field
              label="Additional Notes"
              htmlFor="child-notes"
              error={errors.additionalNotes?.message}
            >
              <textarea
                id="child-notes"
                rows={3}
                {...register("additionalNotes")}
                className={INPUT_CLASS}
                placeholder="Any other important information that would help caregivers provide the best care for your child?"
              />
            </Field>
          </div>
        </SectionCard>

        <SectionCard icon={GraduationCap} title="School Information" accent="violet">
          <div className="space-y-4">
            <label className="flex cursor-pointer items-start gap-3 rounded-2xl bg-violet-50/80 p-4 ring-1 ring-violet-200">
              <input
                type="checkbox"
                {...register("isEnrolled")}
                className="mt-0.5 h-4 w-4 rounded border-neutral-300 accent-violet-600"
              />
              <span className="block text-sm font-medium text-neutral-900">
                Currently enrolled in school
              </span>
            </label>
            {watch("isEnrolled") && (
              <>
                <Field
                  label="Grade"
                  htmlFor="child-grade"
                  error={errors.grade?.message}
                >
                  <input
                    id="child-grade"
                    type="text"
                    {...register("grade")}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label="School Name"
                  htmlFor="child-school-name"
                  error={errors.schoolName?.message}
                >
                  <input
                    id="child-school-name"
                    type="text"
                    {...register("schoolName")}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label="School Phone"
                  htmlFor="child-school-phone"
                  error={errors.schoolPhone?.message}
                >
                  <input
                    id="child-school-phone"
                    type="tel"
                    {...register("schoolPhone")}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label="Teacher Name"
                  htmlFor="child-teacher-name"
                  error={errors.teacherName?.message}
                >
                  <input
                    id="child-teacher-name"
                    type="text"
                    {...register("teacherName")}
                    className={INPUT_CLASS}
                  />
                </Field>
                <Field
                  label="School Nurse Phone"
                  htmlFor="child-school-nurse-phone"
                  error={errors.schoolNursePhone?.message}
                >
                  <input
                    id="child-school-nurse-phone"
                    type="tel"
                    {...register("schoolNursePhone")}
                    className={INPUT_CLASS}
                  />
                </Field>
              </>
            )}
          </div>
        </SectionCard>

        <SectionCard icon={Phone} title="Emergency Contact" accent="rose">
          <div className="space-y-4">
            <Field
              label="Contact Name"
              htmlFor="child-ec-name"
              error={errors.emergencyContactName?.message}
            >
              <input
                id="child-ec-name"
                type="text"
                {...register("emergencyContactName")}
                className={INPUT_CLASS}
                placeholder="Enter emergency contact name"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Phone Number"
                htmlFor="child-ec-phone"
                error={errors.emergencyContactPhone?.message}
              >
                <input
                  id="child-ec-phone"
                  type="tel"
                  {...register("emergencyContactPhone")}
                  className={INPUT_CLASS}
                />
              </Field>
              <Field
                label="Relationship"
                htmlFor="child-ec-relationship"
                error={errors.emergencyContactRelationship?.message}
              >
                <input
                  id="child-ec-relationship"
                  type="text"
                  {...register("emergencyContactRelationship")}
                  className={INPUT_CLASS}
                />
              </Field>
            </div>
          </div>
        </SectionCard>

        <SectionCard icon={PhoneCall} title="Secondary Emergency Contact" accent="rose">
          <div className="space-y-4">
            <Field
              label="Contact Name"
              htmlFor="child-ec2-name"
              error={errors.secondaryEmergencyContactName?.message}
            >
              <input
                id="child-ec2-name"
                type="text"
                {...register("secondaryEmergencyContactName")}
                className={INPUT_CLASS}
                placeholder="Enter secondary emergency contact name"
              />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field
                label="Phone Number"
                htmlFor="child-ec2-phone"
                error={errors.secondaryEmergencyContactPhone?.message}
              >
                <input
                  id="child-ec2-phone"
                  type="tel"
                  {...register("secondaryEmergencyContactPhone")}
                  className={INPUT_CLASS}
                />
              </Field>
              <Field
                label="Relationship"
                htmlFor="child-ec2-relationship"
                error={errors.secondaryEmergencyContactRelationship?.message}
              >
                <input
                  id="child-ec2-relationship"
                  type="text"
                  {...register("secondaryEmergencyContactRelationship")}
                  className={INPUT_CLASS}
                />
              </Field>
            </div>
          </div>
        </SectionCard>

        <div className="flex items-center justify-end gap-3">
          <Button asChild variant="ghost">
            <Link href="/dashboard">Cancel</Link>
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting || createMutation.isPending}
          >
            {createMutation.isPending ? "Creating…" : "Create Profile"}
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
